import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { MessageService, SlbSeverity } from '@slb-dls/angular-material/notification';
import { EventDataIteration, EventDetails, GlobalData, Media } from '../../shared/models/eventDetails';
import { CameraProfileService } from '../../shared/services/camera-profile.service';
import { BehaviorSubject, EMPTY, Subject, catchError, filter, of, switchMap, takeUntil, tap } from 'rxjs';
import {
  BOOKMARK_ERROR,
  BPOS,
  BPOS_UNIT,
  BVEL,
  BVEL_UNIT,
  EVENTERROR,
  EVENT_CSV_DOWNLOAD_HEADING,
  EVENT_FILTER,
  FILE_DOWNLOAD_ERROR,
  NONE,
  RPM,
  RPM_UNIT,
  SPPA,
  SPPA_UNIT,
  WAIT_FOR_REPORT_CREATION_MSG,
} from '../../shared/constants/camera-profile-constant';
import { PageEvent } from '@angular/material/paginator';
import { PopUpService } from '../../shared/services/pop-up.service';
import { DashboardService } from '../../shared/services/dashboard.service';
import { DashboardConstants } from '../../shared/constants/dashboard-constants';
import { GlobalViewService } from 'src/app/shared/services/global-view.service';
import { DatePipe } from '@angular/common';
import { TimeZoneService } from '../../shared/services/time-zone.service';
import { TimeZone } from '../../shared/models/dashabordAlertData';
import { CameraConstants } from 'src/app/shared/constants/camera-constant';
import { UserSettingsService } from 'src/app/shared/services/user-settings.service';
import { UnitsData } from 'src/app/shared/models/unit';
import { Bookmark } from '../../../../src/app/shared/models/global-view';
import { GlobalEventGridComponent } from '../global-event-grid/global-event-grid.component';

@Component({
  selector: 'app-global-event',
  templateUrl: './global-event.component.html',
  styleUrls: ['./global-event.component.scss'],
})
export class GlobalEventComponent implements OnInit, OnDestroy {
  @ViewChild(GlobalEventGridComponent) globalEventGridComponent!: GlobalEventGridComponent;

  public violationData: EventDetails[];
  public viewMode = 'grid';
  public selectedFiles: EventDetails[];
  public checkAll: boolean;
  public violationDataList: EventDetails[];
  public isLoading: boolean;
  public currentZone = '';
  public refreshedTimezone = '';
  public totalRecords = 0;
  public reviewFilterList = EVENT_FILTER;
  public paginatedData: PageEvent = JSON.parse(JSON.stringify(DashboardConstants.DEFAULT_PAGINATION));
  public isDownloadStarted = false;
  public noEventData: boolean;
  public eventId: number;
  public startDate: Date;
  public endDate: Date;
  public isDisableButton: boolean;
  public filterData: { [key: string]: string[] | boolean };
  public disableCSV = false;
  public bookMarkOnly: boolean;

  private destroyed = new Subject();
  private pageNumber: number;
  private recordsPerPage: number;
  private eventState = new BehaviorSubject(this.paginatedData);
  private eventState$ = this.eventState.asObservable();
  private showAllEvents: string;
  private isPaginated = false;
  private unitSelected: UnitsData;

  constructor(
    private cameraProfileService: CameraProfileService,
    private dashboardService: DashboardService,
    private messageService: MessageService,
    private popupService: PopUpService,
    private globalViewService: GlobalViewService,
    private timeZoneService: TimeZoneService,
    private userSettingService: UserSettingsService,
    private datePipe: DatePipe
  ) {}

  ngOnInit(): void {
    this.isLoading = true;
    this.selectedFiles = [];
    this.noEventData = false;
    this.isDisableButton = false;
    this.pageNumber = DashboardConstants.PAGE_NUMBER;
    this.recordsPerPage = DashboardConstants.RECORDS_PER_PAGE;
    this.bookMarkOnly =
      sessionStorage.getItem('bookMarkOnly') && sessionStorage.getItem('bookMarkOnly') === 'true'
        ? true
        : sessionStorage.getItem('bookMarkOnly') && sessionStorage.getItem('bookMarkOnly') === 'false'
        ? false
        : this.bookMarkOnly;
    this.timeZoneService.timeZoneDetails$
      .pipe(
        filter((zone: TimeZone) => !!Object.keys(zone).length),
        tap((timeZoneDetails: TimeZone) => {
          this.currentZone = timeZoneDetails.value;
        }),
        switchMap(() =>
          this.userSettingService.unitSelected$.pipe(
            tap((value: string) => {
              if (value) {
                this.unitSelected = JSON.parse(value);
              }
            })
          )
        )
      )
      .subscribe();
  }

  public switchView(event: { value: string }): void {
    this.viewMode = event.value;
  }

  public downloadSelectedFile(): void {
    const downloadUrls: number[] = [];
    if (this.selectedFiles && this.selectedFiles.length > 0) {
      this.selectedFiles.forEach(file => {
        const medias = file.media.filter((media: Media) => media.fileUrl);
        if (medias.length) {
          downloadUrls.push(file.eventId);
        }
      });
      if (downloadUrls && downloadUrls.length > 0) {
        this.downloadFiles(downloadUrls);
      } else {
        this.messageService.add({ severity: SlbSeverity.Error, summary: FILE_DOWNLOAD_ERROR });
      }
    }
  }

  public async openReviewPopup(): Promise<void> {
    if (this.selectedFiles && this.selectedFiles.length > 0) {
      await this.popupService.openEditReviewConfiguration(this.selectedFiles);
    }
  }

  public getSelectedFiles(value: EventDetails[]): void {
    this.selectedFiles = value;
    if (this.violationDataList.length > this.selectedFiles.length) {
      this.checkAll = false;
    } else if (
      this.violationDataList.length === this.selectedFiles.length &&
      this.violationDataList.length !== 0 &&
      this.selectedFiles.length !== 0
    ) {
      this.checkAll = true;
    }
    this.updateSelection();
  }

  public checkAllFiles(): void {
    if (this.checkAll) {
      this.selectedFiles = this.violationDataList;
      this.violationDataList.map((data: EventDetails) => (data['isChecked'] = true));
      this.violationDataList = [...this.violationDataList];
    } else {
      this.violationDataList.map((data: EventDetails) => (data['isChecked'] = false));
      this.violationDataList = [...this.violationDataList];
      this.selectedFiles = [];
    }
    this.updateSelection();
  }

  public getPaginatedData(event: PageEvent): void {
    if (this.pageNumber !== event.pageIndex + 1 || this.recordsPerPage !== event.pageSize) {
      this.paginatedData = event;
      this.pageNumber = event.pageIndex + 1;
      this.recordsPerPage = event.pageSize;
      this.selectedFiles = [];
      const isFilter = Object.keys(this.filterData).filter(data => (this.filterData[data] as string[])?.length);
      if (isFilter?.length) {
        this.eventState.next(this.paginatedData);
        this.isDisableButton = false;
      } else {
        this.isDisableButton = true;
      }
    }
  }

  public setEventId(): void {
    this.eventId = 0;
  }

  public bookmarkEvent(event: Bookmark): void {
    const eventId = event.eventId;
    this.globalViewService
      .toggleBookmark(event.eventId)
      .pipe(
        catchError(() => {
          this.messageService.add({
            severity: SlbSeverity.Error,
            summary: BOOKMARK_ERROR + eventId,
          });
          this.globalEventGridComponent.revertToInitialBookmarkedState(event.eventId);

          return EMPTY;
        }),
        takeUntil(this.destroyed)
      )
      .subscribe();
  }

  public switchDays(startDate: Date, endDate: Date): void {
    this.startDate = startDate;
    this.endDate = endDate;
    this.pageNumber = DashboardConstants.PAGE_NUMBER;
    this.recordsPerPage = DashboardConstants.RECORDS_PER_PAGE;
    this.paginatedData = JSON.parse(JSON.stringify(DashboardConstants.DEFAULT_PAGINATION));
    if (this.filterData) {
      const isFilter = Object.keys(this.filterData)?.filter(data => (this.filterData[data] as string[])?.length);
      if (!isFilter?.length) {
        this.violationDataList = [];
        this.totalRecords = 0;
        this.isDisableButton = true;
      }
    }
  }

  public workFlowEventStatusChange(filterData: { [key: string]: string[] | boolean }): void {
    this.filterData = filterData;
    const isFilter = Object.keys(this.filterData).filter(data => (this.filterData[data] as string[])?.length);
    if (!isFilter?.length) {
      this.violationDataList = [];
      this.totalRecords = 0;
      this.isDisableButton = true;
    }
  }

  public setFirstInitialization(startDate: Date, endDate: Date, filterData: { [key: string]: string[] | boolean }): void {
    this.startDate = startDate;
    this.endDate = endDate;
    this.filterData = filterData;
    const isFilter = Object.keys(this.filterData).filter(data => (this.filterData[data] as string[])?.length);
    if (isFilter?.length) {
      this.getEvent();
    }
  }

  public downloadCSV(): void {
    const startDate = this.calculateTimeBasedOnTimezone(this.startDate);
    const endDate = this.calculateTimeBasedOnTimezone(this.endDate);
    const recordsPerPage = 10000;
    const pageNumber = 1;
    this.isDownloadStarted = true;
    this.messageService.add({ severity: SlbSeverity.Info, summary: CameraConstants.CSV_DOWNLOAD_WAIT_MSG, sticky: true, closable: false });
    let allEvents: EventDetails[] = [];
    this.globalViewService
      .getAllEventData(startDate, endDate, this.filterData, this.showAllEvents, pageNumber, recordsPerPage, this.currentZone)
      .pipe(
        tap((value: EventDataIteration) => {
          const columnNames = EVENT_CSV_DOWNLOAD_HEADING;

          if (!value?.totalRecords || !columnNames?.length) {
            return;
          }
          const eventData: EventDetails[] = JSON.parse(JSON.stringify(value.data));
          allEvents = allEvents.concat(eventData);

          if (value.index === value.totalPages - 1) {
            const eventDataGroupByCamera: { [s: string]: EventDetails[] } = allEvents.reduce((r, a) => {
              r[a.equipmentName as string] = r[a.equipmentName as string] || [];
              r[a.equipmentName as string].push(a);

              return r;
            }, Object.create(null));

            for (let i = 0; i < Object.values(eventDataGroupByCamera).length; i++) {
              let blob = new Blob();
              const currentCameraEvents = Object.values(eventDataGroupByCamera)[i] as EventDetails[];
              const csvDataIndividualCamera = this.convertToCsv('', currentCameraEvents, columnNames);
              blob = new Blob(['\ufeff' + csvDataIndividualCamera], { type: 'text/csv;charset=utf-8;' });
              this.downloadData(blob, currentCameraEvents[0] ? (currentCameraEvents[0].equipmentName as string) : '');
            }
          }
        }),
        catchError(() => {
          this.messageService.clear();
          this.messageService.add({ severity: SlbSeverity.Error, summary: CameraConstants.CSV_DOWNLOAD_ERROR_MSG });
          this.isDownloadStarted = false;

          return of<EventDataIteration>({} as EventDataIteration);
        }),
        takeUntil(this.destroyed)
      )
      .subscribe();
  }

  public sendAnalyticsReportOverEmail(): void {
    this.showAllEvents = sessionStorage.getItem('showAllEvents') || '';
    const startDate = this.calculateTimeBasedOnTimezone(this.startDate);
    const endDate = this.calculateTimeBasedOnTimezone(this.endDate);
    this.messageService.add({
      severity: SlbSeverity.Success,
      summary: WAIT_FOR_REPORT_CREATION_MSG,
    });
    this.globalViewService
      .sendAnalyticsReportOverEmail(
        startDate,
        endDate,
        this.bookMarkOnly,
        this.filterData,
        this.showAllEvents,
        this.pageNumber,
        this.recordsPerPage,
        this.currentZone
      )
      .pipe(takeUntil(this.destroyed))
      .subscribe();
  }

  public setBookmarkSelectionInSession(): void {
    sessionStorage.setItem('bookMarkOnly', String(this.bookMarkOnly));
    this.globalViewService.refreshAnalyticsWidgets(String(this.bookMarkOnly));
  }

  ngOnDestroy(): void {
    this.cameraProfileService.setEventTime('');
    this.destroyed.next(true);
    this.destroyed.complete();
  }

  private getEvent(): void {
    let startDate: string;
    let endDate: string;
    this.isLoading = true;
    this.eventState$
      .pipe(
        tap(() => {
          this.isPaginated = true;
        }),
        switchMap(() =>
          this.cameraProfileService.eventData$.pipe(
            tap(_value => {
              this.isLoading = true;
              this.totalRecords = 0;
              this.showAllEvents = sessionStorage.getItem('showAllEvents') || '';
              startDate = this.calculateTimeBasedOnTimezone(this.startDate);
              endDate = this.calculateTimeBasedOnTimezone(this.endDate);
              this.selectedFiles = [];
              this.currentZone = sessionStorage.getItem('timezone') ?? '';
              this.setPagination(_value);
            }),
            switchMap(() =>
              this.globalViewService
                .getEventDetails(
                  startDate,
                  endDate,
                  this.filterData,
                  this.showAllEvents,
                  this.pageNumber,
                  this.recordsPerPage,
                  this.currentZone
                )
                .pipe(
                  tap((eventData: GlobalData) => {
                    if (eventData?.data) {
                      this.setEventData(eventData);
                    } else {
                      this.violationDataList = [];
                      this.totalRecords = 0;
                    }
                    if (eventData?.totalRecords === 0) {
                      this.violationDataList = [];
                    }
                    this.isLoading = false;
                  }),
                  catchError(() => {
                    this.messageService.add({ severity: SlbSeverity.Error, summary: EVENTERROR });
                    this.isLoading = false;

                    return of<string>('');
                  }),
                  takeUntil(this.destroyed)
                )
            )
          )
        ),
        takeUntil(this.destroyed)
      )
      .subscribe();
  }

  private setEventData(eventData: GlobalData): void {
    this.violationDataList = eventData?.data;
    this.totalRecords = eventData?.totalRecords;
    this.checkAll = false;
    this.isDisableButton = false;
    this.violationDataList?.forEach(data => {
      data.icon = this.dashboardService.getIconImg(data);
      data.label = this.dashboardService.getReason(data);
      data.rigState = this.dashboardService.getRigEventDetails(data);
      data.quality = this.dashboardService.formatLabel(data.quality ?? '');
      if (data?.isChecked) {
        data.isChecked = false;
      }

      return data;
    });
  }

  private setPagination(value: boolean): void {
    if (!this.isPaginated && value) {
      this.paginatedData.pageIndex = 0;
      this.paginatedData.pageSize = DashboardConstants.RECORDS_PER_PAGE;
      this.paginatedData.length = DashboardConstants.PAGE_NUMBER;
      this.recordsPerPage = DashboardConstants.RECORDS_PER_PAGE;
      this.pageNumber = DashboardConstants.PAGE_NUMBER;
    } else {
      this.isPaginated = false;
    }
  }

  private calculateTimeBasedOnTimezone(date: Date): string {
    if (date) {
      const dateBdOnChosedTimezone = new DatePipe('en-Us').transform(date, 'EEEE, MMMM d, y, h:mm:ss a zzzz', this.currentZone);
      if (dateBdOnChosedTimezone) {
        const basedonCurrentTimezone: Date = new Date(dateBdOnChosedTimezone);

        return basedonCurrentTimezone.toISOString();
      }

      return '';
    }

    return '';
  }

  private downloadFiles(eventId: number[]): void {
    this.cameraProfileService
      .downloadMediaAsZip('', '', eventId)
      .pipe(
        tap((data: string) => {
          if (data && data !== '') {
            this.downloadZipFile(data);
          } else {
            this.messageService.add({ severity: SlbSeverity.Error, summary: FILE_DOWNLOAD_ERROR });
          }
        }),
        catchError(err => {
          console.error(err);
          throw err;
        }),
        takeUntil(this.destroyed)
      )
      .subscribe();
  }

  private updateSelection(): void {
    this.violationDataList.forEach((data: EventDetails) => {
      data.isChecked = false;
    });
    this.selectedFiles.forEach((file: EventDetails) => {
      this.violationDataList.forEach((data: EventDetails) => {
        if (file.eventId === data.eventId) {
          data.isChecked = true;
        }
      });
    });
  }

  private downloadZipFile(url: string): void {
    const blob = new Blob([url], {
      type: 'application/zip',
    });
    const bloburl = window.URL.createObjectURL(blob);
    const filename = 'SLB Edge-Vision Intelligence-global';
    const dwldLink = document.createElement('a');

    dwldLink.setAttribute('href', bloburl);
    dwldLink.setAttribute('download', filename + '.zip');
    dwldLink.setAttribute('target', '_blank');
    dwldLink.click();
    dwldLink.remove();
  }

  private convertToCsv(prevCsvData: string, objArray: EventDetails[], headerList: string[]): string {
    let str = '';
    if (!prevCsvData) {
      let row = '';
      headerList.forEach((value: string) => {
        value = this.addUnit(value);
        value = this.formatHeader(value);
        row += value.charAt(0).toUpperCase() + value.slice(1) + ',';
      });
      row = row.slice(0, -1);
      str += row + '\r\n';
    }

    str += this.extractData(objArray, headerList, this.currentZone);

    return str;
  }

  private addUnit(header: string): string {
    switch (header) {
      case BPOS:
        return BPOS + this.userSettingService.getUnit(BPOS_UNIT, this.unitSelected.unitData);
      case BVEL:
        return BVEL + this.userSettingService.getUnit(BVEL_UNIT, this.unitSelected.unitData);
      case RPM:
        return RPM + this.userSettingService.getUnit(RPM_UNIT, this.unitSelected.unitData);
      case SPPA:
        return SPPA + this.userSettingService.getUnit(SPPA_UNIT, this.unitSelected.unitData);
    }

    return header;
  }

  private formatHeader(header: string): string {
    switch (header) {
      case 'rigName':
        return 'Site';
      case 'gatewayName':
        return 'Gateway Name';
      case 'equipmentName':
        return 'Camera Name';
      case 'peopleLimit':
        return 'People limit';
      default:
        return header;
    }
  }

  private extractData(objArray: EventDetails[], headerList: string[], currentZone: string): string {
    let str = '';

    objArray.forEach((eventValues: EventDetails) => {
      let line = '';
      headerList.forEach((header: string) => {
        line += this.getPropertyValue(eventValues, header, currentZone) + ',';
      });
      line = line.replace(/,\s*$/, '');
      str += line + '\r\n';
    });

    return str;
  }

  private getPropertyValue(value: EventDetails, propertyValue: string, currentZone: string): string {
    switch (propertyValue) {
      case 'peopleCount':
        return value?.peopleCount?.toString() ?? '0';

      case 'peopleLimit':
        return value?.peopleLimit != null && value.peopleLimit !== 0 ? String(value.peopleLimit) : '';

      case 'confidence':
        return value?.confidence?.toString() ?? '0';

      case 'reason':
        if (value.rigState) {
          value.rigState = this.dashboardService.getRigEventDetails(value);
        }
        if (!value.rigState || value.rigState === CameraConstants.NoRigData || (value && value.workflowType === '')) {
          return CameraConstants.NoRigData;
        } else {
          let tag = this.dashboardService.getReason(value);
          tag += ': ' + value.rigState;
          tag = tag.replace(/,/g, '|');

          return tag;
        }
      case 'quality':
        let quality = '';
        if (value?.quality) {
          quality = this.dashboardService.formatLabel(value?.quality);
        }

        return quality;

      case 'category':
        return !value.category || value.category === NONE
          ? DashboardConstants.NOT_REVIEWED
          : value.category.charAt(0).toUpperCase() + value.category.slice(1).toLowerCase();

      case 'comment':
        return value.lastComment ? value.lastComment : '';

      case 'type':
        return this.dashboardService.getWorkFlowType(value) + ' events';

      case 'timestamp':
        const dateconversion = this.datePipe.transform(new Date(value.timestamp), 'MM/dd/yyyy hh:mm:ss a', currentZone);

        return dateconversion ? dateconversion : '';

      case 'imageFilename':
        const image = value.media.find(x => x.type === 'image');

        return image ? image.fileUrl.split('download/')[1] : '';

      case 'videoFilename':
        const video = value.media.find(x => x.type === 'video');

        return video ? video.fileUrl.split('download/')[1] : '';

      case 'BPOS':
        return value.rigData?.BPOS_m !== null && typeof value.rigData?.BPOS_m !== 'undefined' && !isNaN(value.rigData?.BPOS_m)
          ? this.setUnitSelected(value.rigData.BPOS_m, BPOS_UNIT)
          : '';

      case 'BVEL':
        return value.rigData?.BVEL_mps != null && typeof value.rigData?.BVEL_mps !== 'undefined' && !isNaN(value.rigData?.BVEL_mps)
          ? this.setUnitSelected(value.rigData.BVEL_mps, BVEL_UNIT)
          : '';

      case 'RPM':
        return value.rigData?.RPM !== null && typeof value.rigData?.RPM !== 'undefined' && !isNaN(value.rigData?.RPM)
          ? this.setUnitSelected(value.rigData.RPM, RPM_UNIT)
          : '';

      case 'SPPA':
        return value.rigData?.SPPA_Pa !== null && typeof value.rigData?.SPPA_Pa !== 'undefined' && !isNaN(value.rigData?.SPPA_Pa)
          ? this.setUnitSelected(value.rigData.SPPA_Pa, SPPA_UNIT)
          : '';

      default:
        return value ? (value[propertyValue as keyof EventDetails] as string) : '';
    }
  }

  private setUnitSelected(currValue: number, defaultUnit: string): string {
    const getSelectedUnit = this.userSettingService.getUnit(defaultUnit, this.unitSelected.unitData);

    return this.userSettingService.siToChosenUnit(currValue, defaultUnit, getSelectedUnit);
  }

  private downloadData(blob: Blob, cameraName: string): void {
    const dwldLink = document.createElement('a');
    const url = URL.createObjectURL(blob);
    const isSafariBrowser = navigator.userAgent.indexOf('Safari') !== -1 && navigator.userAgent.indexOf('Chrome') === -1;
    const filename = `${cameraName} - Events - ${new Date().toLocaleString()}`;

    if (isSafariBrowser) {
      dwldLink.setAttribute('target', '_blank');
    }
    dwldLink.setAttribute('href', url);
    dwldLink.setAttribute('download', filename + '.csv');
    dwldLink.style.visibility = 'hidden';
    document.body.appendChild(dwldLink);
    dwldLink.click();
    document.body.removeChild(dwldLink);
    this.isDownloadStarted = false;
    this.messageService.clear();
  }
}
